import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { Settings, Brain, CheckCircle, Loader2, MessageSquare, Send, Phone, Wifi } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AI_OPTIONS = [
  { id: 'claude', name: 'Claude Sonnet 4.5', provider: 'Anthropic', desc: 'Mas preciso para ECUs, mejor calidad. ~1-2 cent/analisis', color: 'text-orange-400' },
  { id: 'gemini', name: 'Gemini 2.0 Flash', provider: 'Google', desc: 'Gratis, rapido, buena calidad general', color: 'text-blue-400' },
  { id: 'openai', name: 'GPT-5.2', provider: 'OpenAI', desc: 'Alta calidad, mas caro. ~2-3 cent/analisis', color: 'text-green-400' },
  { id: 'emergent', name: 'Emergent GPT-4o', provider: 'Emergent', desc: 'Backup con key universal', color: 'text-purple-400' },
  { id: 'ollama', name: 'Qwen 3.5 35B (Local)', provider: 'Ollama Local', desc: 'IA local con RTX 5090. Gratis, privado, sin limites.', color: 'text-yellow-400' },
];

const AdminSettingsPage = () => {
  const { token } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedAi, setSelectedAi] = useState('claude');

  // SMS config
  const [smsPrimaryUrl, setSmsPrimaryUrl] = useState('');
  const [smsPrimaryKey, setSmsPrimaryKey] = useState('');
  const [smsBackupUrl, setSmsBackupUrl] = useState('');
  const [smsBackupKey, setSmsBackupKey] = useState('');
  const [testPhone, setTestPhone] = useState('');
  const [testing, setTesting] = useState(null); // 'primary' | 'backup' | null
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/settings`).then(r => {
      setSettings(r.data);
      setSelectedAi(r.data.ai_provider || 'claude');
      setSmsPrimaryUrl(r.data.sms_primary_url || '');
      setSmsPrimaryKey(r.data.sms_primary_key || '');
      setSmsBackupUrl(r.data.sms_backup_url || '');
      setSmsBackupKey(r.data.sms_backup_key || '');
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const saveAll = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, {
        ai_provider: selectedAi,
        sms_primary_url: smsPrimaryUrl,
        sms_primary_key: smsPrimaryKey,
        sms_backup_url: smsBackupUrl,
        sms_backup_key: smsBackupKey,
      });
      toast.success('Configuracion guardada');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al guardar'); }
    setSaving(false);
  };

  const testSms = async (gateway) => {
    if (!testPhone.trim()) { toast.error('Introduce un telefono para el test'); return; }
    setTesting(gateway);
    setTestResult(null);
    try {
      const res = await axios.post(`${API}/admin/settings/test-sms`, {
        phone: testPhone, gateway,
        sms_primary_url: smsPrimaryUrl, sms_primary_key: smsPrimaryKey,
        sms_backup_url: smsBackupUrl, sms_backup_key: smsBackupKey,
      });
      setTestResult(res.data);
      if (res.data.success && !res.data.mock) toast.success(`SMS enviado via ${gateway}!`);
      else if (res.data.mock) toast.warning(`SMS en MOCK mode (no enviado realmente)`);
      else toast.error(`Fallo: ${res.data.error || res.data.detail || 'Error'}`);
    } catch (e) { toast.error('Error al enviar test'); setTestResult({ success: false, error: 'Connection error' }); }
    setTesting(null);
  };

  if (loading) return <Layout><div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div></Layout>;

  return (
    <Layout>
      <div className="max-w-3xl mx-auto" data-testid="admin-settings-page">
        <div className="flex items-center gap-3 mb-6">
          <Settings className="w-6 h-6 text-primary" />
          <h1 className="font-heading text-3xl font-bold text-white">CONFIGURACION</h1>
        </div>

        {/* AI Provider */}
        <div className="bg-surface border border-white/10 rounded-md p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Brain className="w-5 h-5 text-primary" />
            <h2 className="font-heading text-white text-lg font-bold">PROVEEDOR DE IA</h2>
          </div>
          <div className="space-y-3 mb-4">
            {AI_OPTIONS.map(opt => (
              <button key={opt.id} onClick={() => setSelectedAi(opt.id)}
                className={`w-full text-left p-4 rounded-md border transition-all ${selectedAi === opt.id ? 'border-primary bg-primary/10' : 'border-white/10 bg-surface-highlight hover:border-white/20'}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <span className={`font-heading font-bold ${opt.color}`}>{opt.name}</span>
                    <span className="text-zinc-600 text-xs ml-2">{opt.provider}</span>
                    <p className="text-zinc-500 text-xs mt-1">{opt.desc}</p>
                  </div>
                  {selectedAi === opt.id && <CheckCircle className="w-5 h-5 text-primary" />}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* SMS Gateway Config */}
        <div className="bg-surface border border-white/10 rounded-md p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <MessageSquare className="w-5 h-5 text-green-400" />
            <h2 className="font-heading text-white text-lg font-bold">SMS GATEWAY</h2>
          </div>

          {/* Primary */}
          <div className="mb-4 p-4 bg-green-500/5 border border-green-500/20 rounded-md">
            <div className="flex items-center gap-2 mb-3">
              <Wifi className="w-4 h-4 text-green-400" />
              <span className="text-green-400 text-sm font-bold">PRINCIPAL</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-zinc-500 text-xs">IP / URL</label>
                <input value={smsPrimaryUrl} onChange={e => setSmsPrimaryUrl(e.target.value)} placeholder="http://10.5.20.11"
                  className="w-full bg-surface border border-white/10 rounded px-3 py-2 text-white text-sm mt-1" data-testid="sms-primary-url" />
              </div>
              <div>
                <label className="text-zinc-500 text-xs">API Key</label>
                <input value={smsPrimaryKey} onChange={e => setSmsPrimaryKey(e.target.value)} placeholder="sms_..."
                  className="w-full bg-surface border border-white/10 rounded px-3 py-2 text-white text-sm mt-1 font-mono text-xs" data-testid="sms-primary-key" />
              </div>
            </div>
          </div>

          {/* Backup */}
          <div className="mb-4 p-4 bg-yellow-500/5 border border-yellow-500/20 rounded-md">
            <div className="flex items-center gap-2 mb-3">
              <Wifi className="w-4 h-4 text-yellow-400" />
              <span className="text-yellow-400 text-sm font-bold">BACKUP</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-zinc-500 text-xs">IP / URL</label>
                <input value={smsBackupUrl} onChange={e => setSmsBackupUrl(e.target.value)} placeholder="http://10.5.20.9"
                  className="w-full bg-surface border border-white/10 rounded px-3 py-2 text-white text-sm mt-1" />
              </div>
              <div>
                <label className="text-zinc-500 text-xs">API Key</label>
                <input value={smsBackupKey} onChange={e => setSmsBackupKey(e.target.value)} placeholder="sms_..."
                  className="w-full bg-surface border border-white/10 rounded px-3 py-2 text-white text-sm mt-1 font-mono text-xs" />
              </div>
            </div>
          </div>

          {/* Test SMS */}
          <div className="p-4 bg-surface-highlight border border-white/10 rounded-md">
            <div className="flex items-center gap-2 mb-3">
              <Phone className="w-4 h-4 text-primary" />
              <span className="text-white text-sm font-bold">TEST SMS</span>
            </div>
            <div className="flex gap-2 mb-3">
              <input value={testPhone} onChange={e => setTestPhone(e.target.value)} placeholder="+34679703426"
                className="flex-1 bg-surface border border-white/10 rounded px-3 py-2 text-white text-sm font-mono" data-testid="test-phone" />
              <Button onClick={() => testSms('primary')} disabled={testing === 'primary'} className="bg-green-600 hover:bg-green-500 text-white text-xs" data-testid="test-primary">
                {testing === 'primary' ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Send className="w-3 h-3 mr-1" />Principal</>}
              </Button>
              <Button onClick={() => testSms('backup')} disabled={testing === 'backup'} variant="outline" className="border-yellow-500/30 text-yellow-400 text-xs" data-testid="test-backup">
                {testing === 'backup' ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Send className="w-3 h-3 mr-1" />Backup</>}
              </Button>
            </div>
            {testResult && (
              <div className={`p-3 rounded text-sm ${testResult.success && !testResult.mock ? 'bg-green-500/10 text-green-400' : testResult.mock ? 'bg-yellow-500/10 text-yellow-400' : 'bg-red-500/10 text-red-400'}`} data-testid="test-result">
                {testResult.success && !testResult.mock && `SMS enviado OK via ${testResult.gateway} (ID: ${testResult.request_id})`}
                {testResult.mock && `MOCK MODE: SMS simulado, no enviado realmente. Verificar modem.`}
                {!testResult.success && `FALLO: ${testResult.error || testResult.detail || 'Error desconocido'}`}
              </div>
            )}
          </div>
        </div>

        {/* Save Button */}
        <Button onClick={saveAll} disabled={saving} className="bg-primary hover:bg-primary/90 text-white w-full h-12 text-lg" data-testid="save-settings">
          {saving ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <CheckCircle className="w-5 h-5 mr-2" />}
          Guardar Toda la Configuracion
        </Button>

        {settings?.updated_at && (
          <p className="text-zinc-600 text-xs mt-3 text-center">
            Ultima actualizacion: {new Date(settings.updated_at).toLocaleString('es-ES')} por {settings.updated_by || 'admin'}
          </p>
        )}
      </div>
    </Layout>
  );
};

export default AdminSettingsPage;
